import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Icon from "../AppIcon";
import { useLogout } from "pages/authentication-modal/useLogout";
import toast from "react-hot-toast";
import { useGetLoggedInUser } from "../../pages/dashboard-overview/useGetLoggedInUser";
import {
  getActiveCompany,
  getMyCompanies,
  selectCompany,
} from "api/company.api";
import { QUERY_KEYS } from "config/queryKeys";
import { ROUTES } from "config/routes";
import { invalidateCompanyScopedQueries } from "lib/queryHelpers";
import { useLanguage } from "hooks/useLanguage";
import LanguageSwitcher from "./LanguageSwitcher";

const SidebarContext = createContext();

export const useSidebar = () => {
  const context = useContext(SidebarContext);

  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }

  return context;
};

export const SidebarProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  const toggleSidebar = () => setIsOpen(!isOpen);
  const closeSidebar = () => setIsOpen(false);
  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  return (
    <SidebarContext.Provider
      value={{
        isOpen,
        isCollapsed,
        isHovered,
        setIsHovered,
        toggleSidebar,
        closeSidebar,
        toggleCollapse,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

const SidebarItem = ({ icon, label, path, isActive, onClick, tooltip }) => {
  const { isCollapsed, isHovered } = useSidebar();
  const showLabel = !isCollapsed || isHovered;

  return (
    <div className="relative group">
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className={`
          w-full flex items-center px-4 py-3 text-start rounded-xl transition-all duration-200 hover-lift
          ${
            isActive
              ? "bg-primary text-primary-foreground shadow-hero-cta"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          }
          ${!showLabel ? "justify-center" : "justify-start"}
        `}
        title={!showLabel ? label : ""}
      >
        <Icon
          name={icon}
          size={20}
          className={`flex-shrink-0 transition-all duration-200 ${
            showLabel ? "me-3" : ""
          }`}
        />

        <span
          className={`font-medium text-sm whitespace-nowrap transition-all duration-200 ${
            showLabel ? "opacity-100 w-auto" : "opacity-0 w-0 overflow-hidden"
          }`}
        >
          {label}
        </span>
      </button>

      {!showLabel && tooltip && (
        <div className="absolute start-full ms-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-50">
          {tooltip}
        </div>
      )}
    </div>
  );
};

const Sidebar = () => {
  const {
    isOpen,
    isCollapsed,
    isHovered,
    setIsHovered,
    closeSidebar,
    toggleCollapse,
  } = useSidebar();

  const { t } = useTranslation("sidebar");
  const { t: tCommon } = useTranslation("common");
  const { isRTL } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = useMemo(
    () => [
      {
        icon: "LayoutDashboard",
        label: t("nav.dashboard"),
        path: "/dashboard",
        section: "sales",
      },
      {
        icon: "Scale",
        label: t("nav.accounting"),
        path: ROUTES.ACCOUNTING,
        section: "sales",
      },
      {
        icon: "Package",
        label: t("nav.products"),
        path: "/product-management",
        section: "sales",
      },
      {
        icon: "Users",
        label: t("nav.clients"),
        path: "/client-management",
        section: "sales",
      },
      {
        icon: "FolderOpen",
        label: t("nav.invoices"),
        path: "/invoice-management-hub",
        section: "sales",
      },
      {
        icon: "FileText",
        label: t("nav.createInvoice"),
        path: "/invoice-creation-wizard",
        section: "sales",
      },
      {
        icon: "Wallet",
        label: t("nav.payments"),
        path: "/payment-management",
        section: "sales",
      },
      {
        icon: "RotateCcw",
        label: t("nav.refunds"),
        path: "/refunds",
        section: "sales",
      },
      {
        icon: "Upload",
        label: t("nav.uploadInvoice"),
        path: ROUTES.INVOICE_UPLOAD,
        section: "sales",
      },
      {
        icon: "LayoutDashboard",
        label: t("nav.dashboard"),
        path: "/purchases/dashboard",
        section: "purchases",
      },
      {
        icon: "Truck",
        label: t("nav.vendors"),
        path: "/vendor-management",
        section: "purchases",
      },
      {
        icon: "ShoppingCart",
        label: t("nav.purchaseOrders"),
        path: "/purchase-orders",
        section: "purchases",
      },
      {
        icon: "FileText",
        label: t("nav.expenses"),
        path: "/expenses",
        section: "purchases",
      },
      {
        icon: "Wallet",
        label: t("nav.payments"),
        path: "/purchases/payments",
        section: "purchases",
      },
      {
        icon: "RotateCcw",
        label: t("nav.refunds"),
        path: "/purchases/refunds",
        section: "purchases",
      },
      {
        icon: "Settings",
        label: t("nav.companySettings"),
        path: "/company-settings",
        section: "admin",
      },
      {
        icon: "Settings",
        label: t("nav.deviceSettings"),
        path: "/device-settings",
        section: "admin",
      },
      {
        icon: "Building2",
        label: t("nav.branches"),
        path: ROUTES.BRANCH_MANAGEMENT,
        section: "admin",
      },
      {
        icon: "Users",
        label: t("nav.users"),
        path: "/user-management",
        section: "admin",
      },
      {
        icon: "FileSearch",
        label: t("nav.auditLogs"),
        path: "/audit-logs",
        section: "compliance",
      },
    ],
    [t],
  );

  const handleNavigation = (item) => {
    closeSidebar();
    setIsHovered(false);

    setTimeout(() => {
      navigate(item.path);
    }, 400);
  };

  const salesItems = useMemo(
    () => navigationItems.filter((item) => item.section === "sales"),
    [navigationItems],
  );

  const purchasesItems = useMemo(
    () => navigationItems.filter((item) => item.section === "purchases"),
    [navigationItems],
  );

  const adminItems = useMemo(
    () => navigationItems.filter((item) => item.section === "admin"),
    [navigationItems],
  );

  const complianceItems = useMemo(
    () => navigationItems.filter((item) => item.section === "compliance"),
    [navigationItems],
  );

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        closeSidebar();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [closeSidebar]);

  const showLabels = !isCollapsed || isHovered;

  const collapseIcon = isCollapsed
    ? isRTL
      ? "ChevronLeft"
      : "ChevronRight"
    : isRTL
      ? "ChevronRight"
      : "ChevronLeft";

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-[9998] md:hidden transition-opacity duration-400"
          onClick={closeSidebar}
        />
      )}

      <aside
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`
          fixed top-0 start-0 h-full bg-white dark:bg-gray-900 border-e border-border z-[9999] transition-all duration-300 ease-in-out
          ${
            isOpen
              ? "translate-x-0"
              : "max-md:ltr:-translate-x-full max-md:rtl:translate-x-full"
          }
          ${!isCollapsed || isHovered ? "w-64" : "w-16"}
          md:translate-x-0
        `}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-border">
            {showLabels ? (
              <div className="flex items-center gap-3">
                <img
                  src="/assets/logos/auditai.svg"
                  alt={tCommon("app.brand")}
                  className="h-8"
                />

                <div
                  className={`transition-all duration-300 ${
                    showLabels
                      ? "opacity-100 w-auto"
                      : "opacity-0 w-0 overflow-hidden"
                  }`}
                >
                  <h1 className="text-lg font-semibold text-foreground whitespace-nowrap">
                    {tCommon("app.brand")}
                  </h1>

                  <p className="text-xs text-muted-foreground whitespace-nowrap">
                    {tCommon("app.compliance")}
                  </p>
                </div>
              </div>
            ) : (
              <img
                src="/assets/logos/auditai.svg"
                alt={tCommon("app.brand")}
                className="h-8 mx-auto"
              />
            )}

            <button
              type="button"
              onClick={toggleCollapse}
              aria-label={
                isCollapsed
                  ? t("controls.expandSidebar")
                  : t("controls.collapseSidebar")
              }
              className={`hidden md:flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-all duration-200 ${
                showLabels ? "opacity-100" : "opacity-0"
              }`}
            >
              <Icon
                name={collapseIcon}
                size={16}
                className="text-muted-foreground"
              />
            </button>
          </div>

          <nav className="flex-1 p-4 space-y-6 overflow-y-auto">
            <div className="space-y-1">
              {showLabels && (
                <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-normal mb-1">
                  {t("sections.sales")}
                </p>
              )}

              {salesItems.map((item) => (
                <SidebarItem
                  key={item.path}
                  icon={item.icon}
                  label={item.label}
                  path={item.path}
                  isActive={location.pathname === item.path}
                  onClick={() => handleNavigation(item)}
                  tooltip={item.label}
                />
              ))}
            </div>

            {purchasesItems.length > 0 && (
              <div className="space-y-1">
                {showLabels && (
                  <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-normal mb-1">
                    {t("sections.purchases")}
                  </p>
                )}

                {purchasesItems.map((item) => (
                  <SidebarItem
                    key={item.path}
                    icon={item.icon}
                    label={item.label}
                    path={item.path}
                    isActive={location.pathname === item.path}
                    onClick={() => handleNavigation(item)}
                    tooltip={item.label}
                  />
                ))}
              </div>
            )}

            <div className="space-y-1">
              {showLabels && (
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-normal mb-3 transition-opacity duration-300">
                  {t("sections.administration")}
                </h3>
              )}

              {adminItems.map((item) => (
                <SidebarItem
                  key={item.path}
                  icon={item.icon}
                  label={item.label}
                  path={item.path}
                  isActive={location.pathname === item.path}
                  onClick={() => handleNavigation(item)}
                  tooltip={item.label}
                />
              ))}
            </div>

            <div className="space-y-1">
              {showLabels && (
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-normal mb-3 transition-opacity duration-300">
                  {t("sections.compliance")}
                </h3>
              )}

              {complianceItems.map((item) => (
                <SidebarItem
                  key={item.path}
                  icon={item.icon}
                  label={item.label}
                  path={item.path}
                  isActive={location.pathname === item.path}
                  onClick={() => handleNavigation(item)}
                  tooltip={item.label}
                />
              ))}
            </div>
          </nav>

          <div className="p-4 border-t border-border">
            <ProfileMenu />
          </div>
        </div>
      </aside>
    </>
  );
};

const getCompaniesArray = (data) =>
  data?.data?.companies || data?.companies || data?.data || [];

const getCompanyRecord = (entry) => entry?.company || entry;

const getCompanyId = (entry) => {
  const company = getCompanyRecord(entry);

  return company?._id || company?.id || entry?.companyId;
};

const getCompanyName = (entry) => {
  const company = getCompanyRecord(entry);

  return company?.legalName || company?.name;
};

const ProfileMenu = () => {
  const { t } = useTranslation("sidebar");
  const { t: tCommon } = useTranslation("common");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate: logout } = useLogout();
  const { data: user, refetch } = useGetLoggedInUser();

  const { isCollapsed, isHovered } = useSidebar();
  const [isOpen, setIsOpen] = useState(false);

  const showLabels = !isCollapsed || isHovered;

  const { data: activeCompanyData } = useQuery({
    queryKey: QUERY_KEYS.companies.active(),
    queryFn: getActiveCompany,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: companies = [] } = useQuery({
    queryKey: QUERY_KEYS.companies.mine(),
    queryFn: getMyCompanies,
    select: getCompaniesArray,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

  const { mutate: switchCompany, isPending: isSwitchingCompany } = useMutation({
    mutationFn: selectCompany,

    onSuccess: () => {
      invalidateCompanyScopedQueries(queryClient);
      toast.success(t("profile.companySwitched"));
      setIsOpen(false);
      navigate(ROUTES.DASHBOARD, { replace: true });
    },

    onError: (error) => {
      toast.error(error?.message || t("profile.companySwitchFailed"));
    },
  });

  const activeCompany =
    user?.activeCompany ||
    user?.company ||
    activeCompanyData?.data?.company ||
    activeCompanyData?.company ||
    activeCompanyData?.data ||
    null;

  const activeCompanyId =
    activeCompany?._id || activeCompany?.id || user?.companyId;

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!showLabels) {
      setIsOpen(false);
    }
  }, [showLabels]);

  const handleLogout = () => {
    logout({
      onError: (error) => {
        const isAuthError = error?.response?.message?.includes("logged in");

        if (isAuthError) {
          toast.error(t("profile.authRequired"), {
            id: "auth-error",
          });
        }
      },
    });
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={t("controls.profileMenu")}
        className={`
          w-full flex items-center p-3 rounded-xl hover:bg-muted transition-all duration-200
          ${!showLabels ? "justify-center" : "justify-between"}
        `}
      >
        <div className={`flex items-center ${!showLabels ? "" : "gap-3"}`}>
          <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-medium text-secondary-foreground">
              {user?.name
                ?.split(" ")
                ?.map((name) => name?.[0])
                ?.join("")
                ?.toUpperCase() || t("profile.user").charAt(0)}
            </span>
          </div>

          {showLabels && (
            <div className="text-start transition-all duration-300 opacity-100 w-auto">
              <p className="text-sm font-medium text-foreground whitespace-nowrap">
                {user?.name || t("profile.user")}
              </p>

              <p className="text-xs text-muted-foreground whitespace-nowrap truncate max-w-[150px]">
                {activeCompany?.legalName ||
                  activeCompany?.name ||
                  user?.roles?.[0] ||
                  t("profile.member")}
              </p>
            </div>
          )}
        </div>

        {showLabels && (
          <Icon
            name="ChevronUp"
            size={16}
            className={`text-muted-foreground transition-all duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      {isOpen && (
        <div
          className={`
            absolute bottom-full mb-2 bg-white dark:bg-zinc-900 border border-border rounded-xl shadow-lg z-50
            ${!showLabels ? "start-full ms-2 w-56" : "inset-x-0"}
          `}
          role="menu"
        >
          <div className="p-2 space-y-1">
            <div className="px-3 py-2">
              <p className="text-xs text-muted-foreground mb-1">
                {t("profile.company")}
              </p>

              <p className="text-sm font-medium text-foreground truncate">
                {activeCompany?.legalName ||
                  activeCompany?.name ||
                  t("profile.noCompanySelected")}
              </p>

              {companies.length > 0 && (
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {companies.map((entry) => {
                    const companyId = getCompanyId(entry);
                    const isActive = companyId && companyId === activeCompanyId;

                    return (
                      <button
                        key={companyId || getCompanyName(entry)}
                        type="button"
                        disabled={isSwitchingCompany || isActive || !companyId}
                        onClick={() => switchCompany(companyId)}
                        className="w-full text-start px-2 py-1.5 text-xs rounded-md hover:bg-muted disabled:opacity-50"
                      >
                        <span className="flex items-center justify-between gap-2">
                          <span className="block truncate">
                            {getCompanyName(entry) ||
                              t("profile.unnamedCompany")}
                          </span>

                          {isActive && (
                            <Icon
                              name="Check"
                              size={14}
                              className="flex-shrink-0 text-primary"
                            />
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="border-t border-border my-1" />

            <button
              type="button"
              disabled
              aria-label={tCommon("theme.comingSoon")}
              title={tCommon("theme.comingSoon")}
              className="w-full flex items-center justify-between px-3 py-2 text-sm text-muted-foreground rounded-lg opacity-60 cursor-not-allowed"
            >
              <span className="flex items-center min-w-0">
                <Icon name="Moon" size={16} className="me-3 flex-shrink-0" />

                <span>{tCommon("theme.switchToDark")}</span>
              </span>

              <span className="text-[10px] font-medium uppercase tracking-wide">
                {tCommon("theme.comingSoonShort")}
              </span>
            </button>

            <div className="border-t border-border my-1" />

            <LanguageSwitcher />

            <div className="border-t border-border my-1" />

            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center px-3 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
            >
              <Icon name="LogOut" size={16} className="me-3" />
              {tCommon("actions.signOut")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
